import React, { useMemo, useState, useEffect } from "react";
import { Breadcrumb, Select, Spin, Alert } from "antd";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axios';
import "./AllAnouncement.css";
import Heading from "../components/Heading";
import VillageIllustration from "../components/VillageIllustration";
import EmptyState from '../components/EmptyState';

const { Option } = Select;

const categoryColor = (cat) => {
  switch (cat) {
    case "बैठक":
    case "Meeting":
      return {
        light: "#2B7FFF",
        dark: "#002371"
      }; // blue gradient
    case "ग्राम सभा":
    case "Gram Sabha":
      return {
        light: "#00C950",
        dark: "#006D29"
      }; // green gradient
    case "कार्यक्रम":
    case "Program":
      return {
        light: "#ff6b35",
        dark: "#cc3300"
      }; // orange gradient
    default:
      return {
        light: "#6b7280",
        dark: "#374151"
      }; // gray gradient
  }
};

const AllAnouncement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // default selected financial year - pick one present in data
  const availableYears = useMemo(() => {
    const s = Array.from(new Set(data.map((d) => d.financialYear || '2025-2026')));
    return s.sort();
  }, [data]);

  const [selectedYear, setSelectedYear] = useState(
    availableYears.length ? availableYears[availableYears.length - 1] : "2025-2026"
  );

  // when available years update (for example after API fetch), ensure selectedYear is valid
  useEffect(() => {
    if (availableYears.length && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[availableYears.length - 1]);
    }
  }, [availableYears, selectedYear]);

  const filtered = useMemo(
    () => data.filter((a) => (a.financialYear || '2025-2026') === selectedYear),
    [selectedYear, data]
  );

  // Fetch programme highlights from API and prefer API data over static samples
  useEffect(() => {
    let mounted = true;
    const fetchHighlights = async () => {
      setLoading(true);
      setError(null);
      try {
        const languageParam = i18n.language === 'mr' ? 'Marathi' : 'English';
        const resp = await axiosInstance.get('/programme-highlights/', {
          params: {  language: languageParam }
        });

        let items = [];
        if (Array.isArray(resp.data)) items = resp.data;
        else if (resp.data && typeof resp.data === 'object') {
          items = resp.data.results || resp.data.data || null;
          if (!items) {
            const values = Object.values(resp.data);
            if (Array.isArray(values) && values.length && typeof values[0] === 'object') items = values;
          }
        }

        if (!mounted) return;
        if (Array.isArray(items)) {
          const mapped = items
            .filter(it => it.is_active !== false)
            .map(it => ({
              id: it.id,
              category: it.type || it.category || (i18n.language === 'mr' ? 'बैठक' : 'Meeting'),
              title: it.title || it.description || '',
              date: it.date || '',
              description: it.title || it.description || '',
              financialYear: it.financialYear || '2025-2026',
              link: it.link || null,
            }));
          setData(mapped);
        } else {
          // unexpected shape
          // eslint-disable-next-line no-console
          console.error('/programme-highlights/ unexpected shape', resp.data);
          setData([]);
        }
      } catch (err) {
        setError(err.message || 'Failed to load programme highlights');
        setData([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchHighlights();
    return () => { mounted = false; };
  }, [i18n.language]);

  const handleBreadcrumbClick = (path) => {
    navigate(path);
  };

  return (
    <>
    <div className="section-padding bg-image-section">
      {loading && <div style={{ padding: 12 }}><Spin /></div>}
      {error && <div style={{ padding: 8 }}><Alert type="warning" message={error} /></div>}
      <div className="top-row">
        <div className="breadcrumb-and-title">
          <Breadcrumb separator=">" className="custom-breadcrumb" aria-label="breadcrumb">
            <Breadcrumb.Item>
              <span 
                onClick={() => handleBreadcrumbClick('/')} 
                style={{ cursor: 'pointer', color: '#3b4a3b' }}
              >
                {i18n.language === 'mr' ? 'मुख्यपृष्ठ' : 'Home'}
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{i18n.language === 'mr' ? 'महत्त्वाची सूचना' : 'Important Announcements'}</Breadcrumb.Item>
          </Breadcrumb>

          <Heading content={i18n.language === 'mr' ? 'महत्त्वाची सूचना' : 'Important Announcements'} />
        </div>

        <div className="filter-area">
          <Select
            value={selectedYear}
            onChange={(val) => setSelectedYear(val)}
            style={{ minWidth: 200 }}
            aria-label="Financial Year"
          >
            {/* Example static options; you can replace with dynamic list */}
            {["2024-2025", "2025-2026", "2026-2027"].map((y) => (
              <Option key={y} value={y}>
                Financial Year {y}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="py-8">
          <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
        </div>
      ) : (
        <div className="cards-grid" role="list">
          {filtered.map((item) => {
            const colors = categoryColor(item.category);
            return (
              <article
                key={item.id}
                className="announcement-card"
                role="listitem"
                aria-labelledby={`ann-${item.id}-title`}
              >

                <div className="card-body">
                  <div className="card-header">
                <span
                  className="category-pill"
                  style={{ 
                    '--pill-color': colors.light,
                    '--pill-color-dark': colors.dark
                  }}
                >
                  {item.category}
                </span>
                    <h3 className="card-title">{item.date}</h3>
                   
                  </div>

                {item.description && <p className="card-desc">{item.description}</p>}
              </div>
            </article>
            );
          })}
        </div>
      )}

      <div className="page-footer">
        {/* pagination or load more could go here */}
      </div>
    </div>
      <VillageIllustration />
    </>
  );
};

export default AllAnouncement;